import { useTranslation } from 'react-i18next';
import { AlertOctagon, AlertTriangle, Info } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { AdminStateMismatch } from '@/types/admin-trace';

/**
 * StateMismatch-Banner (Spec 143 §5.3): DDB-vs-CloudWatch-Diskrepanzen,
 * ganz oben in der Trace-Ansicht. Severity-Optik:
 * critical = rot, warning = gelb, info = dezent.
 * Kommt nur mit ?include=cloudwatch — ohne CW-Daten immer leer.
 */
const SEVERITY_CONFIG = {
  critical: {
    className: 'border-red-400 bg-red-50 text-red-800',
    Icon: AlertOctagon,
  },
  warning: {
    className: 'border-amber-300 bg-amber-50 text-amber-800',
    Icon: AlertTriangle,
  },
  info: {
    className: 'border-border bg-muted/50 text-muted-foreground',
    Icon: Info,
  },
} as const;

export function StateMismatchBanner({ mismatches }: { mismatches: AdminStateMismatch[] }) {
  const { t } = useTranslation('transactions');

  if (mismatches.length === 0) return null;

  return (
    <div className="space-y-2" data-testid="state-mismatch-banner">
      {mismatches.map((m, idx) => {
        const config = SEVERITY_CONFIG[m.severity] ?? SEVERITY_CONFIG.info;
        const { Icon } = config;
        return (
          <div
            key={`${m.phase}-${m.type}-${idx}`}
            className={cn('flex items-start gap-2 rounded-md border px-3 py-2 text-sm', config.className)}
            data-severity={m.severity}
          >
            <Icon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
            <div className="min-w-0 space-y-0.5">
              <p className="font-medium">
                {t('trace.mismatches.title')} — {m.phase} ({t(`trace.mismatches.types.${m.type}`, m.type)})
              </p>
              <p className="break-words">{m.description}</p>
              <p className="text-xs opacity-80">
                DDB: <span className="break-all font-mono">{m.ddbState}</span>
                {' · '}CW: <span className="break-all font-mono">{m.cwState ?? '—'}</span>
              </p>
            </div>
          </div>
        );
      })}
    </div>
  );
}
